import { useCallback, useEffect, useMemo, useState } from 'react'

import type { PageMetaData } from '@utils/ApiClient'

import { DxTradeOrderStatuses, type DxTradeOrderType } from '@model/DxTrade.types'

import { useLocallyPersistedState } from '@hooks/useStorage'

import { DXTRADE_POSITION_EFFECT, type DxtradePositionEffect } from '../../utils/constants'
import { useDxtradeOrdersHistory } from './useDxtradeOrdersHistory'

export const HISTORY_DEFAULT_PAGE_SIZE = 10

export type HistoryFilters = {
  side?: 'BUY' | 'SELL'
  ofType?: DxTradeOrderType
  issuedFrom?: string
  issuedTo?: string
  positionEffect?: DxtradePositionEffect
}

type HistoryPagination = {
  pageIndex: number
  pageSize: number
}

type UsePortfolioHistoryParams = {
  accountLogin?: string
  isHistoryTabActive: boolean
  pageSize?: number
}

export const usePortfolioHistory = ({
  accountLogin,
  isHistoryTabActive,
  pageSize = HISTORY_DEFAULT_PAGE_SIZE,
}: UsePortfolioHistoryParams) => {
  const [historyFilters, setHistoryFiltersState] = useLocallyPersistedState<HistoryFilters>(
    'portfolio-history-filters',
    { positionEffect: DXTRADE_POSITION_EFFECT.CLOSE }
  )
  const [historyPagination, setHistoryPagination] = useState<HistoryPagination>({
    pageIndex: 1,
    pageSize,
  })

  const setHistoryFilters = useCallback<React.Dispatch<React.SetStateAction<HistoryFilters>>>(
    (updater) => {
      setHistoryFiltersState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        const keys: (keyof HistoryFilters)[] = [
          'side',
          'ofType',
          'positionEffect',
          'issuedFrom',
          'issuedTo',
        ]
        return keys.every((k) => prev[k] === next[k]) ? prev : next
      })
    },
    []
  )

  useEffect(() => {
    setHistoryPagination((prev) => {
      if (prev.pageIndex === 1 && prev.pageSize === pageSize) {
        return prev
      }
      return {
        pageIndex: 1,
        pageSize,
      }
    })
  }, [historyFilters, pageSize])

  const historyQuery = useMemo(() => {
    return {
      status: DxTradeOrderStatuses.COMPLETED,
      side: historyFilters.side,
      ofType: historyFilters.ofType,
      positionEffect: historyFilters.positionEffect,
      issuedFrom: historyFilters.issuedFrom,
      issuedTo: historyFilters.issuedTo,
    }
  }, [
    historyFilters.side,
    historyFilters.ofType,
    historyFilters.positionEffect,
    historyFilters.issuedFrom,
    historyFilters.issuedTo,
  ])

  const shouldLoadHistory = isHistoryTabActive && !!accountLogin
  const { data: historyOrdersResp, isLoading: isHistoryLoading } = useDxtradeOrdersHistory(
    shouldLoadHistory ? accountLogin ?? '' : '',
    historyQuery
  )

  const historyOrdersRaw = useMemo(() => {
    const orders = historyOrdersResp?.orders
    if (!orders?.length) return []
    return [...orders].reverse()
  }, [historyOrdersResp])

  const isHistoryInitialLoading = shouldLoadHistory && isHistoryLoading

  const historyPageMeta: PageMetaData | undefined = useMemo(() => {
    if (!historyOrdersRaw.length) return undefined

    const totalResults = historyOrdersRaw.length
    const pageSizeCurrent = historyPagination.pageSize
    const totalPages = Math.max(1, Math.ceil(totalResults / pageSizeCurrent))
    const pageIndexCurrent = Math.min(historyPagination.pageIndex, totalPages)

    return {
      itemsCount: Math.min(
        pageSizeCurrent,
        Math.max(0, totalResults - (pageIndexCurrent - 1) * pageSizeCurrent)
      ),
      pageIndex: pageIndexCurrent,
      pageSize: pageSizeCurrent,
      totalPages,
      totalResults,
    }
  }, [historyOrdersRaw.length, historyPagination.pageIndex, historyPagination.pageSize])

  const historyOrdersPage = useMemo(() => {
    if (!historyOrdersRaw.length) return historyOrdersRaw

    const pageIndexCurrent = historyPageMeta?.pageIndex ?? 1
    const pageSizeCurrent = historyPageMeta?.pageSize ?? historyPagination.pageSize
    const start = (pageIndexCurrent - 1) * pageSizeCurrent
    const end = start + pageSizeCurrent

    return historyOrdersRaw.slice(start, end)
  }, [historyOrdersRaw, historyPageMeta, historyPagination.pageSize])

  const handleHistoryPageChanged = useCallback(
    (pageIndex: number, pageSizeParam?: number) => {
      setHistoryPagination((prev) => {
        const nextPageSize = pageSizeParam ?? prev.pageSize
        const totalResults = historyOrdersRaw.length
        const totalPages = Math.max(1, Math.ceil(totalResults / nextPageSize))
        const clampedPageIndex = Math.min(Math.max(pageIndex ?? 1, 1), totalPages)

        return {
          pageIndex: clampedPageIndex,
          pageSize: nextPageSize,
        }
      })
    },
    [historyOrdersRaw.length]
  )

  return {
    historyFilters,
    setHistoryFilters,
    historyOrdersRaw,
    historyOrdersPage,
    historyPageMeta,
    isHistoryInitialLoading,
    handleHistoryPageChanged,
  }
}
